import { z } from 'zod';
import { defineFeature } from '../core/defineFeature.js';
import { FeatureType } from '../core/types.js';
const Navbar = { id: 5, name: 'Navbar' };
/** Stable identifiers for the navbar items the client knows how to render. Behavior (route, modal, trigger) is resolved from the key in the client, never from stored data. */
export const NAVBAR_ITEM_KEYS = ['home', 'support', 'documents', 'chat', 'faq'];
export const NavbarItemKeySchema = z.enum(NAVBAR_ITEM_KEYS);
/** Allowlist of icon identifiers; the client maps each to a component. */
export const NAVBAR_ICON_KEYS = ['home', 'help-circle', 'folder', 'insights-chat', 'quiz'];
export const NavbarIconKeySchema = z.enum(NAVBAR_ICON_KEYS);
export const NavbarItemSchema = z.object({
    key: NavbarItemKeySchema,
    name: z.string().trim().min(1).max(40),
    icon: NavbarIconKeySchema,
    /** Lower renders first. Only relative order matters; gaps are allowed. */
    ordinal: z.number().int().min(0),
    enabled: z.boolean(),
});
/** Strict schema, used to validate writes: an unknown key is rejected. */
const NavbarItemsSchema = z
    .array(NavbarItemSchema)
    .refine((items) => new Set(items.map((i) => i.key)).size === items.length, {
    message: 'Navbar item keys must be unique',
});
/**
 * Drops entries whose key this build does not know (a newer or retired
 * catalog) so one stray entry cannot invalidate the whole stored list. Only
 * for reads; entries that are otherwise malformed are kept and still fail
 * validation.
 */
export function dropUnknownNavbarItems(value) {
    if (!Array.isArray(value))
        return value;
    return value.filter((entry) => !(typeof entry === 'object' &&
        entry !== null &&
        'key' in entry &&
        typeof entry.key === 'string' &&
        !NavbarItemKeySchema.safeParse(entry.key).success));
}
/** Lenient schema, used to validate reads: unknown keys are filtered out first. */
export const NavbarItemsReadSchema = z.preprocess(dropUnknownNavbarItems, NavbarItemsSchema);
/**
 * Catalog of known items with today's defaults and render order. Doubles as
 * the default feature value and as the source for items missing from a stored
 * list (see {@link resolveNavbarItems}).
 */
export const NAVBAR_ITEM_DEFAULTS = [
    { key: 'home', name: 'Home', icon: 'home', ordinal: 0, enabled: true },
    { key: 'support', name: 'Support', icon: 'help-circle', ordinal: 1, enabled: true },
    { key: 'documents', name: 'Documents', icon: 'folder', ordinal: 2, enabled: true },
    { key: 'chat', name: 'Insights Chat', icon: 'insights-chat', ordinal: 3, enabled: true },
    { key: 'faq', name: 'FAQ', icon: 'quiz', ordinal: 4, enabled: false },
];
/**
 * Normalizes a stored list against the catalog: drops keys the catalog does
 * not know, appends catalog items the stored list lacks (with their defaults,
 * after every stored item), orders by ordinal, and renumbers ordinals 0..n-1.
 * A scope's override replaces the whole list, so without this an item added to
 * the catalog later would never appear for scopes that already have a list.
 */
export function resolveNavbarItems(stored) {
    const storedByKey = new Map(stored.map((item) => [item.key, item]));
    const maxStoredOrdinal = stored.reduce((max, item) => Math.max(max, item.ordinal), -1);
    let nextOrdinal = maxStoredOrdinal + 1;
    const merged = NAVBAR_ITEM_DEFAULTS.map((fallback, catalogIndex) => {
        const existing = storedByKey.get(fallback.key);
        if (existing)
            return { item: existing, catalogIndex };
        return { item: { ...fallback, ordinal: nextOrdinal++ }, catalogIndex };
    });
    return merged
        .sort((a, b) => a.item.ordinal - b.item.ordinal || a.catalogIndex - b.catalogIndex)
        .map(({ item }, index) => ({ ...item, ordinal: index }));
}
export const navbarItems = defineFeature({
    type: FeatureType.Json,
    propName: 'navbar_items',
    uiName: 'navbarItems',
    displayName: 'Navbar Items',
    description: 'Which items appear in the navbar, in what order. Toggle an item off to hide it and reorder items to change where they render',
    category: Navbar,
    areas: ['application'],
    codec: 'json-string',
    schema: NavbarItemsSchema,
    normalizeStored: dropUnknownNavbarItems,
    flatModelSchema: NavbarItemsReadSchema.default(NAVBAR_ITEM_DEFAULTS.map((item) => ({ ...item }))),
    default: NAVBAR_ITEM_DEFAULTS.map((item) => ({ ...item })),
    isSystem: true,
    editor: 'navbar-items',
});
export const supportIcon = defineFeature({
    type: FeatureType.Markdown,
    propName: 'support_icon',
    uiName: 'supportIcon',
    displayName: 'Support Modal Content',
    description: 'Markdown content shown in the Support modal. The Support navbar item only appears when this is set and the item is enabled in Navbar Items',
    category: Navbar,
    areas: ['application'],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
});
export const applicationTooltip = defineFeature({
    type: FeatureType.String,
    propName: 'application_tooltip',
    uiName: 'applicationTooltip',
    displayName: 'Application Tooltip',
    description: 'Configure tooltip text that appears on fresh session and on hover for navbar application icon. Leave empty to disable tooltip',
    category: Navbar,
    areas: ['application'],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
    flatModelSchema: z.string().nullable(),
});
export const faqChatContextIdentifier = defineFeature({
    type: FeatureType.String,
    propName: 'faq_chat_context_identifier',
    uiName: 'faqChatContextIdentifier',
    displayName: 'FAQ Chat Context Identifier',
    description: "Identifier of the chat context to use as this application's FAQ. The context must be added to the application's Chat Contexts list. The FAQ navbar item only appears when this is set and the item is enabled in Navbar Items.",
    category: Navbar,
    areas: ['application'],
    surfacedIn: ['application'],
    codec: 'identity',
    schema: z.string(),
    default: '',
    isSystem: true,
    editor: 'chat-context-select',
});
export const navbarFeatures = [navbarItems, supportIcon, applicationTooltip, faqChatContextIdentifier];
